/**
 * Events table for the active day.
 *
 * Columns: Name | Start | End | Location | Comment | [remove]. Zebra striping
 * on even rows. Pre-populated from /from-defaults on voyage create (handled
 * by backend); this component only edits and exposes "Reset to defaults" +
 * "Add event" buttons.
 */
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Table, TableHeader, TableBody, TableHead, TableRow, TableCell,
} from "@/components/ui/table";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Plus, RotateCw, Trash2 } from "lucide-react";

const blankEvent = () => ({
  source_event_id: null, name: "",
  start_time: "", end_time: "",
  location: "", comment: "",
});

export default function EventsTable({
  value, onChange, onResetDefaults, resetting, dailyEventsLib,
}) {
  const events = value || [];

  // Auto-sort by start_time ascending for display. Edits operate on the
  // sorted view's indexes but underlying storage stays whatever the parent
  // gives us — we re-emit the sorted list on each change.
  const sortedEvents = [...events].sort((a, b) => {
    const ta = (a.start_time || "99:99");
    const tb = (b.start_time || "99:99");
    if (ta === tb) return (a.name || "").localeCompare(b.name || "");
    return ta < tb ? -1 : 1;
  });

  const updateAt = (idx, patch) => {
    const next = sortedEvents.map((e, i) => (i === idx ? { ...e, ...patch } : e));
    onChange(next);
  };

  const removeAt = (idx) => {
    onChange(sortedEvents.filter((_, i) => i !== idx));
  };

  const addEvent = () => onChange([...sortedEvents, blankEvent()]);

  // Phase 7+++ — "+ Add from library" dropdown. Appends a snapshotted
  // copy of a lib_daily_events doc to today's events[] (idempotent —
  // skipped if the same source_event_id is already present).
  const addFromLibrary = (libId) => {
    if (!libId || libId === "__noop__") return;
    const lib = (dailyEventsLib || []).find((d) => d.id === libId);
    if (!lib) return;
    if (sortedEvents.some((e) => e.source_event_id === libId)) return;
    onChange([
      ...sortedEvents,
      {
        source_event_id: lib.id,
        name: lib.name,
        start_time: lib.default_start_time || "",
        end_time: lib.default_end_time || "",
        location: lib.default_location || "",
        comment: lib.default_comment || "",
      },
    ]);
  };

  // Use sortedEvents for rendering below.
  const displayEvents = sortedEvents;

  return (
    <section className="rounded-lg border bg-card p-4" data-testid="events-block">
      <div className="mb-2 flex items-center justify-between gap-2">
        <h3 className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
          Events ({events.length})
        </h3>
        <div className="flex items-center gap-2">
          <Button
            type="button" variant="outline" size="sm"
            onClick={onResetDefaults}
            disabled={resetting}
            data-testid="reset-events-btn"
          >
            <RotateCw className={`h-3.5 w-3.5 ${resetting ? "animate-spin" : ""}`} />
            Reset to defaults
          </Button>
          {(dailyEventsLib || []).length > 0 && (
            <Select value="__noop__" onValueChange={addFromLibrary}>
              <SelectTrigger
                className="h-8 w-[180px] text-xs"
                data-testid="add-from-library-trigger"
              >
                <SelectValue placeholder="+ Add from library" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__noop__" disabled>
                  Pick an event…
                </SelectItem>
                {(dailyEventsLib || []).map((lib) => (
                  <SelectItem
                    key={lib.id}
                    value={lib.id}
                    data-testid={`add-from-library-${lib.id}`}
                  >
                    {lib.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button type="button" size="sm" onClick={addEvent} data-testid="add-event-btn">
            <Plus className="h-3.5 w-3.5" /> Add event
          </Button>
        </div>
      </div>

      <Table className="text-xs">
        <TableHeader>
          <TableRow className="bg-muted/40">
            <TableHead className="min-w-[180px]">Name</TableHead>
            <TableHead className="w-[90px]">Start</TableHead>
            <TableHead className="w-[90px]">End</TableHead>
            <TableHead className="min-w-[140px]">Location</TableHead>
            <TableHead className="min-w-[160px]">Comment</TableHead>
            <TableHead className="w-[40px]"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {displayEvents.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} className="py-6 text-center text-muted-foreground">
                No events yet — use "Reset to defaults" or "Add event".
              </TableCell>
            </TableRow>
          ) : (
            displayEvents.map((e, idx) => (
              <TableRow key={idx} className="even:bg-gray-50" data-testid={`event-row-${idx}`}>
                <TableCell className="align-middle">
                  <Input
                    value={e.name || ""}
                    onChange={(ev) => updateAt(idx, { name: ev.target.value })}
                    className="h-8 text-xs"
                    placeholder="Event name"
                    data-testid={`event-name-${idx}`}
                  />
                </TableCell>
                <TableCell className="align-middle">
                  <Input
                    value={e.start_time || ""}
                    onChange={(ev) => updateAt(idx, { start_time: ev.target.value })}
                    className="h-8 font-mono text-xs"
                    placeholder="HH:MM"
                    data-testid={`event-start-${idx}`}
                  />
                </TableCell>
                <TableCell className="align-middle">
                  <Input
                    value={e.end_time || ""}
                    onChange={(ev) => updateAt(idx, { end_time: ev.target.value })}
                    className="h-8 font-mono text-xs"
                    placeholder="HH:MM"
                    data-testid={`event-end-${idx}`}
                  />
                </TableCell>
                <TableCell className="align-middle">
                  <Input
                    value={e.location || ""}
                    onChange={(ev) => updateAt(idx, { location: ev.target.value })}
                    className="h-8 text-xs"
                    placeholder="Atrium DK4"
                    data-testid={`event-location-${idx}`}
                  />
                </TableCell>
                <TableCell className="align-middle">
                  <Input
                    value={e.comment || ""}
                    onChange={(ev) => updateAt(idx, { comment: ev.target.value })}
                    className="h-8 text-xs"
                    placeholder="Notes"
                    data-testid={`event-comment-${idx}`}
                  />
                </TableCell>
                <TableCell className="align-middle">
                  <Button
                    type="button" variant="ghost" size="icon"
                    onClick={() => removeAt(idx)}
                    aria-label="Remove event"
                    data-testid={`event-remove-${idx}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </section>
  );
}
